import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Presentation, Download, Loader2, Image as ImageIcon, Palette, 
  LayoutTemplate, Plus, Trash2, GripVertical, Copy, HelpCircle, Minus,
  Undo, Redo, Move
} from 'lucide-react';
import { toast } from 'sonner';
import { slideTemplates } from '../data/slideTemplates';
import SpinningWheel from '../components/SpinningWheel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeaders = () => {
  try {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (error) {
    return {};
  }
};

const DigiSlidesEnhanced = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [editingElement, setEditingElement] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [copiedSlide, setCopiedSlide] = useState(null);
  const [insertAfterIndex, setInsertAfterIndex] = useState(null);
  
  // Undo/Redo History
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  
  // Load BNI template by default
  const bniTemplate = slideTemplates.find(t => t.id === 'bni-meeting');
  const [presentation, setPresentation] = useState(() => ({
    title: bniTemplate?.name || 'My Presentation',
    theme: bniTemplate?.theme || 'modern',
    slides: bniTemplate?.slides || []
  }));

  // Initialize history on mount
  useEffect(() => {
    if (history.length === 0 && presentation.slides.length > 0) {
      setHistory([JSON.parse(JSON.stringify(presentation))]);
      setHistoryIndex(0);
    }
  }, []);

  const [showTemplates, setShowTemplates] = useState(false);
  const [showAddSlide, setShowAddSlide] = useState(false);
  const [showBackgroundPicker, setShowBackgroundPicker] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showWheelHelp, setShowWheelHelp] = useState(false);
  const [backgroundSearch, setBackgroundSearch] = useState('');
  const [imageSearch, setImageSearch] = useState('');
  const [backgroundResults, setBackgroundResults] = useState([]);
  const [imageResults, setImageResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchingImages, setSearchingImages] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingSlideImage, setUploadingSlideImage] = useState(false);
  const [removingBg, setRemovingBg] = useState(null);

  const currentSlide = presentation.slides[currentSlideIndex];

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (historyIndex > 0) {
          setHistoryIndex(prev => prev - 1);
          setPresentation(JSON.parse(JSON.stringify(history[historyIndex - 1])));
          toast.success('Undo');
        }
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        if (historyIndex < history.length - 1) {
          setHistoryIndex(prev => prev + 1);
          setPresentation(JSON.parse(JSON.stringify(history[historyIndex + 1])));
          toast.success('Redo');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [historyIndex, history]);

  // Save to history for undo/redo
  const saveToHistory = (newPresentation) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newPresentation)));
    
    // Keep only last 50 states to prevent memory issues
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryIndex(historyIndex + 1);
    }
    
    setHistory(newHistory);
  };

  const updateSlide = (index, field, value) => {
    setPresentation(prev => {
      const newPresentation = {
        ...prev,
        slides: prev.slides.map((slide, i) => 
          i === index ? { ...slide, [field]: value } : slide
        )
      };
      saveToHistory(newPresentation);
      return newPresentation;
    });
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setPresentation(JSON.parse(JSON.stringify(history[historyIndex - 1])));
      toast.success('Undo');
    } else {
      toast.error('Nothing to undo');
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setPresentation(JSON.parse(JSON.stringify(history[historyIndex + 1])));
      toast.success('Redo');
    } else {
      toast.error('Nothing to redo');
    }
  };

  const loadTemplate = (template) => {
    const newPresentation = {
      title: template.name,
      theme: template.theme,
      slides: template.slides.map(slide => ({ ...slide, backgroundImage: slide.backgroundImage || '' }))
    };
    saveToHistory(newPresentation);
    setPresentation(newPresentation);
    setCurrentSlideIndex(0);
    setShowTemplates(false);
    toast.success(`Template loaded: ${template.name}`);
  };

  // Add blank slide - inherits background from current slide
  const addBlankSlide = (afterIndex = currentSlideIndex) => {
    const referenceSlide = presentation.slides[afterIndex];
    const newSlide = {
      title: 'New Slide',
      content: 'Click to edit',
      layout: 'bni_white',
      backgroundImage: referenceSlide?.backgroundImage || '',
      backgroundColor: referenceSlide?.backgroundColor || '#ffffff'
    };
    
    const newPresentation = {
      ...presentation,
      slides: [...presentation.slides.slice(0, afterIndex + 1), newSlide, ...presentation.slides.slice(afterIndex + 1)]
    };
    saveToHistory(newPresentation);
    setPresentation(newPresentation);
    setCurrentSlideIndex(afterIndex + 1);
    setShowAddSlide(false);
    setInsertAfterIndex(null);
    toast.success('Blank slide added with inherited background!');
  };

  const addTemplateSlide = (template, afterIndex = currentSlideIndex) => {
    const newSlide = { ...template.slides[0] };
    
    setPresentation(prev => ({
      ...prev,
      slides: [...prev.slides.slice(0, afterIndex + 1), newSlide, ...prev.slides.slice(afterIndex + 1)]
    }));
    setCurrentSlideIndex(afterIndex + 1);
    setShowAddSlide(false);
    setInsertAfterIndex(null);
    toast.success('Template slide added!');
  };

  const deleteSlide = (index) => {
    if (presentation.slides.length === 1) {
      toast.error('Cannot delete the last slide');
      return;
    }
    
    const newPresentation = {
      ...presentation,
      slides: presentation.slides.filter((_, i) => i !== index)
    };
    saveToHistory(newPresentation);
    setPresentation(newPresentation);
    
    if (currentSlideIndex >= index && currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
    toast.success('Slide deleted');
  };

  const duplicateSlide = (index) => {
    const slideToDuplicate = { ...presentation.slides[index] };
    
    setPresentation(prev => ({
      ...prev,
      slides: [...prev.slides.slice(0, index + 1), slideToDuplicate, ...prev.slides.slice(index + 1)]
    }));
    setCurrentSlideIndex(index + 1);
    toast.success('Slide duplicated!');
  };

  // Copy slide
  const copySlide = (index) => {
    setCopiedSlide({ ...presentation.slides[index] });
    toast.success('Slide copied to clipboard!');
  };

  // Paste slide
  const pasteSlide = (afterIndex = currentSlideIndex) => {
    if (!copiedSlide) {
      toast.error('No slide in clipboard');
      return;
    }
    
    setPresentation(prev => ({
      ...prev,
      slides: [...prev.slides.slice(0, afterIndex + 1), { ...copiedSlide }, ...prev.slides.slice(afterIndex + 1)]
    }));
    setCurrentSlideIndex(afterIndex + 1);
    toast.success('Slide pasted!');
  };

  // Drag and Drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newSlides = [...presentation.slides];
    const draggedSlide = newSlides[draggedIndex];
    newSlides.splice(draggedIndex, 1);
    newSlides.splice(index, 0, draggedSlide);

    setPresentation(prev => ({
      ...prev,
      slides: newSlides
    }));

    if (currentSlideIndex === draggedIndex) {
      setCurrentSlideIndex(index);
    } else if (draggedIndex < currentSlideIndex && index >= currentSlideIndex) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    } else if (draggedIndex > currentSlideIndex && index <= currentSlideIndex) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }

    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const searchBackground = async () => {
    if (!backgroundSearch.trim()) return;
    
    setSearching(true);
    try {
      const response = await axios.get(
        `${API}/slides/search-images?query=${encodeURIComponent(backgroundSearch)}`
      );
      setBackgroundResults(response.data.images || []);
      if (!response.data.images || response.data.images.length === 0) {
        toast.error('No images found for that search');
      }
    } catch (error) {
      console.error('Image search error:', error);
      toast.error('Failed to search images. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const setBackground = (imageUrl) => {
    updateSlide(currentSlideIndex, 'backgroundImage', imageUrl);
    setShowBackgroundPicker(false);
    toast.success('Background updated!');
  };

  const searchImages = async () => {
    if (!imageSearch.trim()) return;
    
    setSearchingImages(true);
    try {
      const response = await axios.get(
        `${API}/slides/search-images?query=${encodeURIComponent(imageSearch)}`
      );
      setImageResults(response.data.images || []);
      if (!response.data.images || response.data.images.length === 0) {
        toast.error('No images found for that search');
      }
    } catch (error) {
      console.error('Image search error:', error);
      toast.error('Failed to search images. Please try again.');
    } finally {
      setSearchingImages(false);
    }
  };

  const addImageToSlide = (imageUrl) => {
    const currentImages = currentSlide.images || [];
    const newImage = {
      url: imageUrl,
      position: { x: 0, y: 0 },
      size: { width: 300, height: 200 }
    };
    updateSlide(currentSlideIndex, 'images', [...currentImages, newImage]);
    setShowImagePicker(false);
    toast.success('Image added to slide! Drag to reposition.');
  };

  const handleSlideImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingSlideImage(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        addImageToSlide(base64String);
        toast.success('Image uploaded!');
        setUploadingSlideImage(false);
      };
      reader.onerror = () => {
        toast.error('Failed to upload image');
        setUploadingSlideImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload image');
      setUploadingSlideImage(false);
    }
  };

  const removeImageFromSlide = (index) => {
    const currentImages = currentSlide.images || [];
    updateSlide(currentSlideIndex, 'images', currentImages.filter((_, i) => i !== index));
    toast.success('Image removed');
  };

  const removeBackground = async (imageIndex) => {
    setRemovingBg(imageIndex);
    try {
      const currentImages = currentSlide.images || [];
      const imgData = currentImages[imageIndex];
      const imageUrl = typeof imgData === 'string' ? imgData : imgData.url;

      const response = await axios.post(
        `${API}/slides/remove-background`,
        { image: imageUrl },
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        const newImages = currentImages.map((img, i) => {
          if (i === imageIndex) {
            if (typeof img === 'string') {
              return response.data.image;
            } else {
              return {
                ...img,
                url: response.data.image
              };
            }
          }
          return img;
        });
        updateSlide(currentSlideIndex, 'images', newImages);
        toast.success('Background removed!');
      }
    } catch (error) {
      console.error('Background removal error:', error);
      toast.error('Failed to remove background. Please try again.');
    } finally {
      setRemovingBg(null);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setBackground(base64String);
        toast.success('Image uploaded!');
        setUploadingImage(false);
      };
      reader.onerror = () => {
        toast.error('Failed to upload image');
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload image');
      setUploadingImage(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/slides/export`,
        presentation,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${presentation.title}.pptx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Presentation downloaded!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const backgroundColors = [
    { name: 'White', value: '#ffffff' },
    { name: 'Light Gray', value: '#f3f4f6' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Purple', value: '#8b5cf6' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Green', value: '#10b981' },
    { name: 'Dark', value: '#1f2937' },
    { name: 'Black', value: '#000000' }
  ];

  const renderSlidePreview = (slide, index) => {
    return (
      <div
        className="relative aspect-video rounded-lg overflow-hidden"
        style={{
          backgroundColor: slide.backgroundColor || '#ffffff',
          backgroundImage: slide.backgroundImage 
            ? `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${slide.backgroundImage})`
            : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {slide.layout === 'bni_white' && (
          <div className="absolute bottom-0 right-0 w-12 h-6 bg-red-600" style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}></div>
        )}
        
        <div className="p-2 text-xs">
          <div className={`font-bold truncate mb-1 ${
            slide.layout === 'bni_white' ? 'text-red-600' : 
            slide.backgroundImage ? 'text-white' : 'text-gray-900'
          }`}>
            {slide.title}
          </div>
          <div className={`text-[8px] line-clamp-2 ${
            slide.layout === 'bni_white' ? 'text-gray-700' :
            slide.backgroundImage ? 'text-white' : 'text-gray-700'
          }`}>
            {slide.content}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Top Toolbar */}
      <div className="bg-white border-b border-gray-200 shadow-sm z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Presentation className="w-6 h-6 text-purple-600" />
              <input
                type="text"
                value={presentation.title}
                onChange={(e) => setPresentation(prev => ({ ...prev, title: e.target.value }))}
                className="text-xl font-bold border-none focus:outline-none focus:ring-2 focus:ring-purple-500 rounded px-2"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                onClick={undo}
                variant="outline"
                size="sm"
                disabled={historyIndex <= 0}
                title="Undo (Ctrl+Z)"
              >
                <Undo className="w-4 h-4" />
              </Button>
              
              <Button
                onClick={redo}
                variant="outline"
                size="sm"
                disabled={historyIndex >= history.length - 1}
                title="Redo (Ctrl+Y)"
              >
                <Redo className="w-4 h-4" />
              </Button>
              
              <div className="w-px h-6 bg-gray-300" />
              
              <Button
                onClick={() => setShowWheelHelp(true)}
                variant="outline"
                size="sm"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Help
              </Button>
              
              <Button
                onClick={() => setShowTemplates(true)}
                variant="outline"
                size="sm"
              >
                <LayoutTemplate className="w-4 h-4 mr-2" />
                Templates
              </Button>
              
              <Button
                onClick={handleExport}
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exporting...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" /> Download</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Vertical Slide Sorter */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-700">Slides</h3>
              <div className="flex gap-1">
                {copiedSlide && (
                  <Button
                    onClick={() => pasteSlide(currentSlideIndex)}
                    size="sm"
                    variant="outline"
                    className="text-xs"
                  >
                    Paste
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setInsertAfterIndex(currentSlideIndex);
                    setShowAddSlide(true);
                  }}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              {presentation.slides.map((slide, index) => (
                <div key={index} className="relative">
                  <div
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    onClick={() => setCurrentSlideIndex(index)}
                    className={`group relative cursor-pointer rounded-lg border-2 transition-all ${
                      currentSlideIndex === index
                        ? 'border-purple-600 shadow-lg'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="absolute -left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical className="w-4 h-4 text-gray-400" />
                    </div>
                    
                    <div className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded z-10">
                      {index + 1}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copySlide(index);
                        }}
                        className="bg-blue-600 text-white p-1 rounded"
                        title="Copy"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setInsertAfterIndex(index);
                          setShowAddSlide(true);
                        }}
                        className="bg-green-600 text-white p-1 rounded"
                        title="Insert after"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      {presentation.slides.length > 1 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSlide(index);
                          }}
                          className="bg-red-600 text-white p-1 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    
                    {renderSlidePreview(slide, index)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-6xl mx-auto">
            {/* Canvas */}
            <div className="bg-white rounded-xl shadow-2xl mb-6 overflow-hidden">
              <div 
                className="relative aspect-video p-12"
                style={{
                  backgroundColor: currentSlide?.backgroundColor || '#ffffff',
                  backgroundImage: currentSlide?.backgroundImage 
                    ? `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${currentSlide.backgroundImage})`
                    : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                {currentSlide?.layout === 'bni_white' && (
                  <>
                    <div className="absolute bottom-0 right-0 w-64 h-16 bg-red-600" style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}></div>
                    <div className="absolute bottom-4 left-4 text-red-600 font-bold text-4xl" style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold' }}>BNI</div>
                  </>
                )}

                {currentSlide?.layout === 'bni_wheel' ? (
                  <div className="h-full">
                    <h2 className="text-4xl font-bold text-center text-red-600 mb-8">{currentSlide.title}</h2>
                    <SpinningWheel 
                      names={currentSlide.wheelNames || []} 
                      editable={true}
                      onNamesChange={(newNames) => updateSlide(currentSlideIndex, 'wheelNames', newNames)}
                    />
                    <p className="text-2xl text-center text-gray-800 mt-4">{currentSlide.content}</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-8">
                      {editingElement === 'title' ? (
                        <input
                          type="text"
                          value={currentSlide?.title || ''}
                          onChange={(e) => updateSlide(currentSlideIndex, 'title', e.target.value)}
                          onBlur={() => setEditingElement(null)}
                          autoFocus
                          className={`text-4xl font-bold w-full bg-transparent border-2 border-purple-500 rounded px-2 ${
                            currentSlide?.layout === 'bni_white' ? 'text-red-600' : 
                            currentSlide?.backgroundImage ? 'text-white' : 'text-gray-900'
                          }`}
                          style={{ 
                            textShadow: currentSlide?.backgroundImage && currentSlide?.layout !== 'bni_white' ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none'
                          }}
                        />
                      ) : (
                        <h2
                          onClick={() => setEditingElement('title')}
                          className={`text-4xl font-bold cursor-pointer hover:opacity-80 transition-opacity ${
                            currentSlide?.layout === 'bni_white' ? 'text-red-600' : 
                            currentSlide?.backgroundImage ? 'text-white' : 'text-gray-900'
                          }`}
                          style={{ 
                            textShadow: currentSlide?.backgroundImage && currentSlide?.layout !== 'bni_white' ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none'
                          }}
                        >
                          {currentSlide?.title || 'Click to edit'}
                          <span className="ml-2 text-sm text-purple-500 opacity-0 hover:opacity-100">✏️</span>
                        </h2>
                      )}
                    </div>

                    <div>
                      {editingElement === 'content' ? (
                        <textarea
                          value={currentSlide?.content || ''}
                          onChange={(e) => updateSlide(currentSlideIndex, 'content', e.target.value)}
                          onBlur={() => setEditingElement(null)}
                          autoFocus
                          rows={8}
                          className={`text-xl w-full bg-transparent border-2 border-purple-500 rounded px-2 font-sans ${
                            currentSlide?.layout === 'bni_white' ? 'text-gray-900' :
                            currentSlide?.backgroundImage ? 'text-white' : 'text-gray-900'
                          }`}
                          style={{ textShadow: currentSlide?.backgroundImage && currentSlide?.layout !== 'bni_white' ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none' }}
                        />
                      ) : (
                        <div
                          onClick={() => setEditingElement('content')}
                          className={`text-xl space-y-2 cursor-pointer hover:opacity-80 transition-opacity ${
                            currentSlide?.layout === 'bni_white' ? 'text-gray-800' :
                            currentSlide?.backgroundImage ? 'text-white' : 'text-gray-900'
                          }`}
                          style={{ 
                            textShadow: currentSlide?.backgroundImage && currentSlide?.layout !== 'bni_white' ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none',
                            lineHeight: '1.6'
                          }}
                        >
                          {(currentSlide?.content || 'Click to edit').split('\n').map((line, i) => (
                            <div key={i}>{line || '\u00A0'}</div>
                          ))}
                          <span className="text-sm text-purple-500 opacity-0 hover:opacity-100">✏️</span>
                        </div>
                      )}
                    </div>

                    {/* Slide Images - Draggable with Touch Support */}
                    {currentSlide?.images && currentSlide.images.length > 0 && (
                      <div className="mt-6 space-y-4">
                        {currentSlide.images.map((imgData, idx) => {
                          const imageUrl = typeof imgData === 'string' ? imgData : imgData.url;
                          const position = typeof imgData === 'object' && imgData.position ? imgData.position : { x: 0, y: 0 };
                          const size = typeof imgData === 'object' && imgData.size ? imgData.size : { width: 300, height: 200 };
                          
                          return (
                            <div 
                              key={idx} 
                              className="relative group inline-block cursor-move touch-none"
                              draggable
                              onDragEnd={(e) => {
                                const rect = e.currentTarget.parentElement.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const y = e.clientY - rect.top;
                                
                                const newImages = currentSlide.images.map((img, i) => {
                                  if (i === idx) {
                                    return {
                                      url: imageUrl,
                                      position: { x, y },
                                      size: size
                                    };
                                  }
                                  return img;
                                });
                                updateSlide(currentSlideIndex, 'images', newImages);
                              }}
                              onTouchStart={(e) => {
                                const touch = e.touches[0];
                                setIsDraggingImage(true);
                                setDragStartPos({
                                  x: touch.clientX - position.x,
                                  y: touch.clientY - position.y
                                });
                              }}
                              onTouchMove={(e) => {
                                if (!isDraggingImage) return;
                                e.preventDefault();
                                const touch = e.touches[0];
                                const newX = touch.clientX - dragStartPos.x;
                                const newY = touch.clientY - dragStartPos.y;
                                
                                const newImages = currentSlide.images.map((img, i) => {
                                  if (i === idx) {
                                    return {
                                      url: imageUrl,
                                      position: { x: newX, y: newY },
                                      size: size
                                    };
                                  }
                                  return img;
                                });
                                updateSlide(currentSlideIndex, 'images', newImages);
                              }}
                              onTouchEnd={() => {
                                setIsDraggingImage(false);
                              }}
                              style={{
                                position: 'relative',
                                left: `${position.x}px`,
                                top: `${position.y}px`
                              }}
                            >
                              <img 
                                src={imageUrl} 
                                alt="" 
                                className="rounded-lg shadow-lg hover:shadow-xl transition-all"
                                style={{
                                  width: `${size.width}px`,
                                  height: `${size.height}px`,
                                  objectFit: 'contain',
                                  backgroundColor: 'transparent'
                                }}
                              />
                              <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => {
                                    const newSize = { width: size.width + 50, height: size.height + 50 };
                                    const newImages = currentSlide.images.map((img, i) => {
                                      if (i === idx) {
                                        return {
                                          url: imageUrl,
                                          position: position,
                                          size: newSize
                                        };
                                      }
                                      return img;
                                    });
                                    updateSlide(currentSlideIndex, 'images', newImages);
                                  }}
                                  className="bg-blue-600 text-white p-1.5 rounded-full"
                                  title="Increase size"
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => {
                                    if (size.width > 100) {
                                      const newSize = { width: size.width - 50, height: size.height - 50 };
                                      const newImages = currentSlide.images.map((img, i) => {
                                        if (i === idx) {
                                          return {
                                            url: imageUrl,
                                            position: position,
                                            size: newSize
                                          };
                                        }
                                        return img;
                                      });
                                      updateSlide(currentSlideIndex, 'images', newImages);
                                    }
                                  }}
                                  className="bg-orange-600 text-white p-1.5 rounded-full"
                                  title="Decrease size"
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => removeBackground(idx)}
                                  disabled={removingBg === idx}
                                  className="bg-purple-600 text-white p-1.5 rounded-full disabled:opacity-50"
                                  title="Remove background"
                                >
                                  {removingBg === idx ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <ImageIcon className="w-3 h-3" />
                                  )}
                                </button>
                                <button
                                  onClick={() => removeImageFromSlide(idx)}
                                  className="bg-red-600 text-white p-1.5 rounded-full"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                              <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                                <Move className="w-3 h-3" />
                                Drag to move
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Bottom Toolbar */}
            <div className="bg-white rounded-xl shadow-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setShowBackgroundPicker(true)}
                  variant="outline"
                  size="sm"
                >
                  <Palette className="w-4 h-4 mr-2" />
                  Background
                </Button>
                
                <Button
                  onClick={() => setShowImagePicker(true)}
                  variant="outline"
                  size="sm"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Add Image
                </Button>
                
                <Button
                  onClick={() => duplicateSlide(currentSlideIndex)}
                  variant="outline"
                  size="sm"
                >
                  Duplicate
                </Button>
                
                <Button
                  onClick={() => copySlide(currentSlideIndex)}
                  variant="outline"
                  size="sm"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
                
                {copiedSlide && (
                  <Button
                    onClick={() => pasteSlide(currentSlideIndex)}
                    variant="outline"
                    size="sm"
                    className="bg-green-50"
                  >
                    Paste
                  </Button>
                )}
              </div>

              <div className="text-sm font-medium text-gray-600">
                Slide {currentSlideIndex + 1} of {presentation.slides.length}
              </div>

              <div className="w-32"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Spinning Wheel Help Modal */}
      {showWheelHelp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-2xl font-bold">🎡 How to Use the Spinning Wheel</h2>
              <button onClick={() => setShowWheelHelp(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-purple-50 border-l-4 border-purple-600 p-4 rounded">
                <h3 className="font-bold text-purple-900 mb-2">Step 1: Load the BNI Template</h3>
                <p className="text-gray-700">Click "Templates" button → Select "BNI Meeting Template" → The wheel is on the "Door Prize Drawing" slide</p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                <h3 className="font-bold text-blue-900 mb-2">Step 2: Edit Names on the Wheel</h3>
                <p className="text-gray-700 mb-2">The wheel comes with 8 default names. To customize:</p>
                <ol className="list-decimal list-inside space-y-1 text-gray-700 ml-4">
                  <li>Navigate to the "Door Prize Drawing" slide</li>
                  <li>The wheel automatically displays the names</li>
                  <li>During presentation, click "Spin the Wheel" button</li>
                </ol>
              </div>

              <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                <h3 className="font-bold text-green-900 mb-2">Step 3: Spin During Presentation</h3>
                <p className="text-gray-700">When you reach the Door Prize slide, click the "Spin the Wheel" button and watch the magic! 🎉</p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded">
                <h3 className="font-bold text-yellow-900 mb-2">💡 Pro Tips</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Wheel spins for 4 seconds with smooth animation</li>
                  <li>Winner announcement appears with celebration effect</li>
                  <li>Names are randomly selected - fair and fun!</li>
                  <li>Perfect for chapter meetings and events</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Selector Modal */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-2xl font-bold">Choose a Template</h2>
              <button onClick={() => setShowTemplates(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid md:grid-cols-3 gap-6">
                {slideTemplates.map(template => (
                  <div
                    key={template.id}
                    onClick={() => loadTemplate(template)}
                    className="border-2 border-gray-200 rounded-xl overflow-hidden cursor-pointer hover:border-purple-600 hover:shadow-xl transition-all"
                  >
                    <img src={template.thumbnail} alt={template.name} className="w-full h-40 object-cover" />
                    <div className="p-4">
                      <div className="text-xs text-purple-600 font-semibold mb-1">{template.category}</div>
                      <h3 className="font-bold text-gray-900">{template.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{template.slides.length} slides</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Slide Modal */}
      {showAddSlide && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-2xl font-bold">
                Add New Slide {insertAfterIndex !== null && `(After Slide ${insertAfterIndex + 1})`}
              </h2>
              <button onClick={() => { setShowAddSlide(false); setInsertAfterIndex(null); }} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <button
                  onClick={() => addBlankSlide(insertAfterIndex !== null ? insertAfterIndex : currentSlideIndex)}
                  className="w-full p-6 border-2 border-gray-300 rounded-xl hover:border-purple-600 hover:bg-purple-50 transition-all text-left"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-12 bg-gray-100 rounded flex items-center justify-center">
                      <Plus className="w-6 h-6 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">Blank Slide</h3>
                      <p className="text-sm text-gray-600">Inherits background from current slide</p>
                    </div>
                  </div>
                </button>

                {copiedSlide && (
                  <button
                    onClick={() => pasteSlide(insertAfterIndex !== null ? insertAfterIndex : currentSlideIndex)}
                    className="w-full p-6 border-2 border-green-300 bg-green-50 rounded-xl hover:border-green-600 transition-all text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-12 bg-green-100 rounded flex items-center justify-center">
                        <Copy className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Paste Copied Slide</h3>
                        <p className="text-sm text-gray-600">Insert the slide from clipboard</p>
                      </div>
                    </div>
                  </button>
                )}

                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-700 mb-3">Or choose from templates:</h3>
                  <div className="grid grid-cols-2 gap-4 max-h-64 overflow-y-auto">
                    {slideTemplates.map(template => (
                      <div
                        key={template.id}
                        onClick={() => addTemplateSlide(template, insertAfterIndex !== null ? insertAfterIndex : currentSlideIndex)}
                        className="border-2 border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:border-purple-600 transition-all"
                      >
                        <img src={template.thumbnail} alt={template.name} className="w-full h-24 object-cover" />
                        <div className="p-2">
                          <p className="text-sm font-semibold text-gray-900 truncate">{template.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Background Picker Modal */}
      {showBackgroundPicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Change Background</h2>
                <button onClick={() => setShowBackgroundPicker(false)} className="text-gray-500 text-2xl">×</button>
              </div>
              
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Upload Your Image</h3>
                <label className="block">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-purple-600 transition-all">
                    {uploadingImage ? (
                      <>
                        <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-purple-600" />
                        <p className="text-sm text-gray-600">Uploading...</p>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm font-medium text-gray-700">Click to upload image</p>
                        <p className="text-xs text-gray-500 mt-1">PNG, JPG, or WEBP</p>
                      </>
                    )}
                  </div>
                </label>
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Solid Colors</h3>
                <div className="flex flex-wrap gap-3">
                  {backgroundColors.map(color => (
                    <button
                      key={color.value}
                      onClick={() => {
                        updateSlide(currentSlideIndex, 'backgroundColor', color.value);
                        updateSlide(currentSlideIndex, 'backgroundImage', '');
                        setShowBackgroundPicker(false);
                        toast.success('Background color changed!');
                      }}
                      className="w-16 h-16 rounded-lg border-2 border-gray-300 hover:border-purple-600 transition-all"
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Search Images</h3>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={backgroundSearch}
                    onChange={(e) => setBackgroundSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchBackground()}
                    placeholder="Search images..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <Button onClick={searchBackground} disabled={searching} className="bg-purple-600 text-white">
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[50vh]">
              {backgroundResults.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {backgroundResults.map((img, i) => (
                    <div
                      key={i}
                      onClick={() => setBackground(img.url)}
                      className="h-32 rounded-lg overflow-hidden cursor-pointer hover:ring-4 hover:ring-purple-600 transition-all"
                    >
                      <img src={img.thumb || img.url} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ImageIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Search for background images</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Picker Modal - Add Images to Slide */}
      {showImagePicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Add Image to Slide</h2>
                <button onClick={() => setShowImagePicker(false)} className="text-gray-500 text-2xl">×</button>
              </div>
              
              {/* Upload Image */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Upload Your Image</h3>
                <label className="block">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSlideImageUpload}
                    className="hidden"
                    disabled={uploadingSlideImage}
                  />
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-purple-600 transition-all">
                    {uploadingSlideImage ? (
                      <>
                        <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-purple-600" />
                        <p className="text-sm text-gray-600">Uploading...</p>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm font-medium text-gray-700">Click to upload image</p>
                        <p className="text-xs text-gray-500 mt-1">PNG, JPG, or WEBP</p>
                      </>
                    )}
                  </div>
                </label>
              </div>

              {/* Stock Image Search */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Search Stock Images</h3>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={imageSearch}
                    onChange={(e) => setImageSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchImages()}
                    placeholder="Search images..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <Button onClick={searchImages} disabled={searchingImages} className="bg-purple-600 text-white">
                    {searchingImages ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[50vh]">
              {imageResults.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {imageResults.map((img, i) => (
                    <div
                      key={i}
                      onClick={() => addImageToSlide(img.url)}
                      className="h-32 rounded-lg overflow-hidden cursor-pointer hover:ring-4 hover:ring-purple-600 transition-all"
                    >
                      <img src={img.thumb || img.url} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ImageIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Search for stock images or upload your own</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigiSlidesEnhanced;
